import type { Plugin, PluginRegistrationContext } from "../interfaces";
import type { ProjectionGraph } from "../entity/projection-registry";
import type { ProjectionRule } from "../entity/projection-rule";

/**
 * Plugin Manager interface for managing plugin lifecycle
 */
export interface PluginManager {
  registerPlugin(plugin: Plugin): void;

  initializePlugins(context?: PluginRegistrationContext): Promise<void>;

  finalizePluginRegistrations(): Promise<void>;

  getProjectionGraphSnapshot(): ProjectionGraph;

  getProjectionRulesSnapshot(): readonly ProjectionRule[];

  readyPlugins(): Promise<void>;

  startPluginDaemons(): Promise<void>;

  getPlugin(id: string): Plugin | undefined;

  getPluginStatus(id: string): PluginStatus | undefined;

  hasPlugin(id: string): boolean;

  isPluginInitialized(id: string): boolean;

  getAllPluginIds(): string[];

  getAllPlugins(): Map<string, PluginInfo>;

  getFailedPlugins(): Array<{ id: string; error: Error }>;

  disablePlugin(id: string): Promise<void>;

  shutdownPlugins(): Promise<void>;

  on<E extends PluginEvent>(
    event: E,
    listener: (...args: PluginManagerEventMap[E]) => void,
  ): void;

  once<E extends PluginEvent>(
    event: E,
    listener: (...args: PluginManagerEventMap[E]) => void,
  ): void;

  off<E extends PluginEvent>(
    event: E,
    listener: (...args: PluginManagerEventMap[E]) => void,
  ): void;
}

/**
 * Plugin status types
 */
export const PluginStatus = {
  REGISTERED: "registered",
  INITIALIZED: "initialized",
  ERROR: "error",
  DISABLED: "disabled",
} as const;
export type PluginStatus = (typeof PluginStatus)[keyof typeof PluginStatus];

/**
 * Plugin metadata with status
 */
export interface PluginInfo {
  plugin: Plugin;
  status: PluginStatus;
  error?: Error;
  dependencies: string[];
}

/**
 * Plugin lifecycle event types
 */
export const PluginEvent = {
  REGISTERED: "plugin:registered",
  BEFORE_INITIALIZE: "plugin:before_initialize",
  INITIALIZED: "plugin:initialized",
  ERROR: "plugin:error",
  DISABLED: "plugin:disabled",
} as const;
export type PluginEvent = (typeof PluginEvent)[keyof typeof PluginEvent];

/**
 * Typed event map for PluginManager events
 */
export interface PluginManagerEventMap {
  [PluginEvent.REGISTERED]: [pluginId: string, plugin: Plugin];
  [PluginEvent.BEFORE_INITIALIZE]: [pluginId: string, plugin: Plugin];
  [PluginEvent.INITIALIZED]: [pluginId: string, plugin: Plugin];
  [PluginEvent.ERROR]: [pluginId: string, error: Error];
  [PluginEvent.DISABLED]: [pluginId: string];
}
